import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useConferenceContext } from '../../contexts/ConferenceContext';
import ConferenceSettingsNav, { isSettingsLocation } from './ConferenceSettingsNav';

/**
 * The conference context bar.
 *
 * Fourteen screens in this app operate on one conference and identify it only
 * by a `?conferenceId=` in the URL. Before this bar existed, once you were
 * inside one of them there was nothing telling you *which* conference you were
 * looking at, no way to reach the other work you had in it, and no way to move
 * to a different conference short of going back to the dashboard.
 *
 * So the bar answers three questions, in this order:
 *   1. Which conference am I in?           → the name, with a switcher
 *   2. What can I do here?                 → tabs, filtered by my roles *here*
 *   3. Where am I among those?             → the active tab
 *
 * The tab list is built from per-conference roles, which is the whole point:
 * a chair sees the running of the conference, a reviewer sees their queue, an
 * author sees their papers. Someone who is both chair and author in the same
 * conference sees both groups, because that is genuinely their situation.
 *
 * It renders nothing at all when there is no conference in scope, so it can be
 * mounted unconditionally without leaving an empty strip on ordinary pages.
 */

/**
 * Routes that operate on a single conference.
 *
 * These used to disagree about the query parameter — six read `?id=`, three
 * read `?conferenceId=` — so linking between them required knowing each page's
 * private convention, which is why cross-linking barely existed. Every route
 * now reads `conferenceId` (and still accepts `id`, so links already shared or
 * bookmarked keep working), and the navigation emits one name everywhere.
 */
export const CONFERENCE_ROUTES = [
  '/conference-management-dashboard',
  '/submission-tracking-dashboard',
  '/manual-reviewer-assignment-interface',
  '/notification-management-screen',
  '/conference-landing-page',
  '/conference-submissions-overview',
  '/review-schema-builder',
  '/author-submission-portal',
  '/email-template-management-system',
  '/reviewer-dashboard',
];

const ConferenceBar = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { conferenceId, conference, myConferences, canManage, isReviewer, isAuthor, loading } =
    useConferenceContext();

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (!switcherRef?.current?.contains(e?.target)) setSwitcherOpen(false);
    };
    const onEsc = (e) => {
      if (e?.key === 'Escape') setSwitcherOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  // Close the switcher on navigation, otherwise it survives the route change.
  useEffect(() => setSwitcherOpen(false), [location?.pathname, location?.search]);

  const onConferenceRoute = CONFERENCE_ROUTES?.includes(location?.pathname);
  const visible = onConferenceRoute && Boolean(conferenceId) && isAuthenticated;

  // Tell the rest of the page how much chrome is above it, so page-level
  // sticky headers can sit below this bar instead of scrolling under it.
  const currentTabParam = new URLSearchParams(location?.search || '').get('tab') || undefined;
  const settingsOpen = visible && isSettingsLocation(location?.pathname, currentTabParam);

  useEffect(() => {
    const root = document.documentElement;
    // navbar 3.5rem + this bar 2.75rem, plus the settings row's 2.5rem when it
    // is showing — page-level sticky headers subtract this, so getting it wrong
    // makes them either float or hide under the chrome.
    const offset = !visible ? '3.5rem' : settingsOpen ? '8.75rem' : '6.25rem';
    root.style.setProperty('--chrome-offset', offset);
    return () => root.style.setProperty('--chrome-offset', '3.5rem');
  }, [visible, settingsOpen]);
  // A signed-out visitor on a public conference page has no roles and no
  // switcher, so the bar would be an empty strip announcing nothing.
  if (!visible) return null;

  const withId = (path, tab) =>
    `${path}?conferenceId=${conferenceId}${tab ? `&tab=${tab}` : ''}`;

  // Tabs are grouped by the role that grants them. Order is deliberate: running
  // the conference first, then reviewing, then authoring — the same order of
  // responsibility the dashboard uses.
  const groups = [
    canManage && {
      key: 'manage',
      label: t('conferenceBar.groups.manage', 'Running this conference'),
      /* Two tabs, not eight.
         Seven of the original eight — Team, Programme, Announcements,
         Settings, Reviewers, Review form, Emails — are things a chair sets up
         once and then rarely touches. Submissions is where the work happens
         every day. Ranking them equally made the daily screen one of eight
         equal choices and pushed the bar past the width of a laptop.
         The other sections now live under Conference settings, which opens a
         second-level row (ConferenceSettingsNav) once you are inside it.
         Reviewer assignment came out entirely: it is done from the
         submissions table, so the separate screen was a second way to do one
         thing. Its route still works for deep links and for the Assign action
         on the user dashboard. */
      tabs: [
        { path: '/conference-management-dashboard', label: t('conferenceBar.submissions', 'Submissions'), icon: 'FileText' },
        {
          path: '/conference-management-dashboard',
          tab: 'settings',
          label: t('conferenceBar.conferenceSettings', 'Conference settings'),
          icon: 'Settings',
          // Stays lit for every section inside settings, not just ?tab=settings.
          matchSettings: true,
        },
      ],
    },
    isReviewer && {
      key: 'review',
      label: t('conferenceBar.groups.review', 'Reviewing'),
      tabs: [
        { path: '/reviewer-dashboard', label: t('conferenceBar.myReviews', 'My reviews'), icon: 'ClipboardCheck' },
      ],
    },
    // `/author-review-results-dashboard` stays out: it aggregates across every
    // conference and ignores a conference id, so linking it here would promise
    // a scoped view and quietly deliver a global one.
    isAuthor && {
      key: 'author',
      label: t('conferenceBar.groups.author', 'Your papers'),
      tabs: [
        { path: '/author-submission-portal', label: t('conferenceBar.submitPaper', 'Submit a paper'), icon: 'Upload' },
      ],
    },
  ]?.filter(Boolean);

  const tabs = groups?.flatMap((g) => g?.tabs) || [];
  const title = conference?.shortName || conference?.name;

  // No roles here at all (e.g. browsing a public conference page): show the
  // identity strip without inventing actions the person can't take.
  const hasWork = tabs?.length > 0;

  return (
    // Same surface as the navbar it hangs from. It used to be bg-card/95 with a
    // blur, which read as a slightly different colour where the two met — a
    // seam across the top of every conference screen.
    //
    // Note on "dark chrome, light work" (brief §7): the navbar is only chrome
    // over the landing hero. On app screens it follows the theme, because a
    // permanently dark navbar forced the reversed logo in light mode, which was
    // rejected. This bar follows the navbar rather than the brief.
    <div className="sticky top-14 z-header-sub bg-card border-b border-border">
      <div className="page-shell">
        <div className="flex items-center gap-3 h-11">
          {/* Which conference */}
          <div className="relative flex-shrink-0" ref={switcherRef}>
            <button
              onClick={() => setSwitcherOpen((o) => !o)}
              disabled={!myConferences?.length}
              aria-expanded={switcherOpen}
              aria-haspopup="listbox"
              className="flex items-center gap-1.5 max-w-[13rem] px-2 py-1 -ms-2 rounded-md hover:bg-muted transition-colors disabled:hover:bg-transparent disabled:cursor-default"
            >
              <Icon name="FolderOpen" size={14} className="text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-semibold text-foreground truncate">
                {loading && !title ? '…' : title || t('conferenceBar.conference', 'Conference')}
              </span>
              {myConferences?.length > 1 && (
                <Icon
                  name="ChevronDown"
                  size={13}
                  className={`text-muted-foreground flex-shrink-0 transition-transform ${switcherOpen ? 'rotate-180' : ''}`}
                />
              )}
            </button>

            {switcherOpen && myConferences?.length > 0 && (
              <div
                role="listbox"
                className="absolute top-full mt-1 w-64 max-h-80 overflow-y-auto bg-card border border-border rounded-md shadow-elevation-3 py-1 z-dropdown motion-rise"
                style={{ insetInlineStart: 0 }}
              >
                <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {t('conferenceBar.switchTo', 'Your conferences')}
                </div>
                {myConferences?.map((c) => {
                  const current = String(c?.id) === String(conferenceId);
                  return (
                    <button
                      key={c?.id}
                      role="option"
                      aria-selected={current}
                      onClick={() => {
                        setSwitcherOpen(false);
                        // Stay on the same job, change which conference it acts
                        // on — switching context shouldn't also lose your place.
                        navigate(`${location?.pathname}?conferenceId=${c?.id}`);
                      }}
                      className={`w-full text-start px-3 py-2 transition-colors ${
                        current ? 'bg-primary/5' : 'hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-1.5 h-1.5 flex-shrink-0 ${
                            current ? 'bg-primary' : 'bg-transparent'
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-foreground truncate">
                            {c?.shortName || c?.name}
                          </p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {c?.roles?.join(' · ')}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {hasWork && <span className="w-px h-5 bg-border flex-shrink-0" aria-hidden="true" />}

          {/* What can I do here — horizontally scrollable rather than wrapped,
              so the bar keeps a fixed height on narrow screens. */}
          {hasWork && (
            <nav
              aria-label={t('conferenceBar.aria', 'Conference sections')}
              className="flex items-center gap-0.5 overflow-x-auto scrollbar-none"
            >
              {groups?.map((group, gi) => (
                <React.Fragment key={group?.key}>
                  {gi > 0 && (
                    <span className="w-px h-5 bg-border flex-shrink-0 mx-1.5" aria-hidden="true" />
                  )}
                  {group?.tabs?.map((tab) => {
                    // Several tabs share one route and differ only by ?tab=,
                    // so the path alone can't say which one is current.
                    const currentTab = new URLSearchParams(location?.search || '').get('tab') || undefined;
                    const active = tab?.matchSettings
                      ? isSettingsLocation(location?.pathname, currentTab)
                      : location?.pathname === tab?.path && (tab?.tab || undefined) === currentTab;
                    return (
                      <Link
                        key={`${tab?.path}:${tab?.tab || ''}`}
                        to={withId(tab?.path, tab?.tab)}
                        aria-current={active ? 'page' : undefined}
                        title={group?.label}
                        /* §4 tab bar: a 2px primary rule under the active tab,
                           muted on hover. No pill and no fill — a tinted block
                           behind a tab is the shape radius 0 just removed, and
                           at this size it competes with the table beneath. */
                        className={`flex items-center gap-1.5 px-2.5 h-11 -mb-px border-b-2 text-[13px] font-medium whitespace-nowrap transition-colors focus-ring ${
                          active
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={tab?.icon} size={14} />
                        {tab?.label}
                      </Link>
                    );
                  })}
                </React.Fragment>
              ))}
            </nav>
          )}
        </div>
      </div>
      {/* Second level: renders itself only when inside settings. */}
      <ConferenceSettingsNav />
    </div>
  );
};

export default ConferenceBar;
